<script setup lang="ts">
import { useShopwareContext } from '@shopware/composables'

const props = defineProps<{
    redirect?: string
}>()

const { apiClient } = useShopwareContext()

// Fetch enabled OAuth providers
const { data: oauthConfig } = await useAsyncData('oauth-config', async () => {
    try {
        const response = await apiClient.invoke('readOAuthConfig post /oauth/config', {})
        return response.data?.providers || {}
    }
    catch {
        return {}
    }
})

const isValidString = (value: unknown): boolean => {
    return typeof value === 'string' && value.trim() !== ''
}

type OAuthProvider = {
    name: string
    label: string
    icon: string
    /** Brand color for the icon. The sprite renders these icons with currentColor. */
    iconClass?: string
}

const enabledProviders = computed<OAuthProvider[]>(() => {
    // Order follows the design: Google, Apple, Facebook
    const providers: OAuthProvider[] = []

    // Google - requires enabled flag and clientId (secrets in server .env)
    if (oauthConfig.value?.google?.enabled &&
        isValidString(oauthConfig.value.google.clientId)) {
        providers.push({
            name: 'google',
            label: 'Google',
            icon: 'google'
        })
    }

    // Apple - requires enabled flag and clientId (secrets in server .env)
    if (oauthConfig.value?.apple?.enabled &&
        isValidString(oauthConfig.value.apple.clientId)) {
        providers.push({
            name: 'apple',
            label: 'Apple',
            icon: 'apple'
        })
    }

    // Facebook - requires enabled flag and appId (secrets in server .env)
    if (oauthConfig.value?.facebook?.enabled &&
        isValidString(oauthConfig.value.facebook.appId)) {
        providers.push({
            name: 'facebook',
            label: 'Facebook',
            icon: 'facebook',
            iconClass: 'text-[#1877F2]'
        })
    }

    return providers
})

const redirectUrl = computed(() => props.redirect || '/customer')
</script>

<template>
    <div v-if="enabledProviders.length > 0">
        <div class="divider text-sm mt-0 mb-6 before:h-px after:h-px">
            {{ $t('customer.oauth.divider') }}
        </div>
        <div class="flex flex-col gap-4">
            <MiscLink
                v-for="provider in enabledProviders"
                :key="provider.name"
                :href="`/api/oauth/${provider.name}/authorize?redirect=${encodeURIComponent(redirectUrl)}`"
                type="external"
                class="btn btn-outline btn-border btn-block flex items-center gap-2"
            >
                <MiscIcon
                    :name="provider.icon"
                    :class="provider.iconClass"
                    size="md"
                />
                <span>{{ $t('customer.oauth.continueWith', { provider: provider.label }) }}</span>
            </MiscLink>
        </div>
    </div>
</template>
